import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { siteImages } from '../data/mock';
import { Target, Users, Award, Hammer, Wrench, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  useEffect(() => { document.title = 'Our Story | Tool Supply Point'; }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main>
        {/* Banner */}
        <section className="relative h-64 md:h-80 overflow-hidden bg-dark-900">
          <img src={siteImages.aboutBanner} alt="Our story" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark-900 via-dark-900/85 to-dark-900/40" />
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-athletic-600/15 border border-athletic-600/30 rounded-sm text-athletic-400 text-xs font-bold uppercase tracking-widest mb-4">
                <Wrench className="w-3.5 h-3.5" /> Our Story
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white font-bold uppercase mb-3">About Tool Supply Point</h1>
              <p className="text-dark-200 max-w-lg">A no-nonsense supplier of hardware and power tools built to earn their keep.</p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Mission */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <span className="inline-block px-4 py-1.5 bg-athletic-50 text-athletic-700 text-xs font-bold uppercase tracking-widest rounded-sm mb-4">Why we started</span>
              <h2 className="font-display text-3xl lg:text-4xl text-dark-900 font-bold uppercase mb-6">Gear that can <span className="text-athletic-600">take a beating</span></h2>
              <p className="text-dark-600 text-lg leading-relaxed mb-6">
                Tool Supply Point began with one gripe every builder shares: too many tools look the part and quit on the first real job. So we stopped chasing catalogs full of lookalikes and started hand-picking a tight lineup &mdash; measuring gear, hand tools, cordless power tools and rugged storage &mdash; that we would trust on our own bench.
              </p>
              <p className="text-dark-600 leading-relaxed mb-8">
                Every item is chosen for hardened durability, honest value and day-in, day-out reliability. Whether you are wiring a panel, framing a deck or fixing up the garage, we make it easy to buy with confidence and get it shipped fast and free.
              </p>
              <div className="flex flex-wrap gap-4">
                {['Hand-picked lineup', 'Fair, honest pricing', 'Fast free shipping'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 px-4 py-2 bg-dark-50 rounded-sm border border-dark-200">
                    <CheckCircle2 className="w-4 h-4 text-athletic-600" />
                    <span className="text-dark-900 text-sm font-semibold">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-sm overflow-hidden border border-dark-200">
                <img src={siteImages.heroLifestyle} alt="Organized tool wall" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-athletic-600 text-white px-6 py-4 rounded-sm shadow-elevated">
                <p className="font-display font-bold text-2xl">14+</p>
                <p className="text-sm font-bold uppercase">Hand-picked tools</p>
              </div>
            </div>
          </div>

          {/* Core Values */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-athletic-50 text-athletic-700 text-xs font-bold uppercase tracking-widest rounded-sm mb-4">What drives us</span>
              <h2 className="font-display text-3xl lg:text-4xl text-dark-900 font-bold uppercase">Our workshop rules</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Target, title: 'Built to last', desc: 'Every tool is judged on grip, torque, materials and time on the job' },
                { icon: Users, title: 'For real work', desc: 'We shop the way tradespeople do &mdash; for gear that survives the site' },
                { icon: Award, title: 'No filler', desc: 'We only stock tools we would happily hand to a fellow builder' },
                { icon: Hammer, title: 'Straight talk', desc: 'Honest specs, honest prices and support that actually picks up' },
              ].map((value, i) => (
                <div key={i} className="bg-dark-50 border border-dark-200 rounded-sm p-6 hover:border-athletic-400 transition-all group">
                  <div className="w-14 h-14 bg-athletic-50 rounded-sm flex items-center justify-center mb-4 group-hover:bg-athletic-100 transition-colors">
                    <value.icon className="w-7 h-7 text-athletic-700" />
                  </div>
                  <h3 className="font-display font-bold uppercase text-dark-900 text-lg mb-2">{value.title}</h3>
                  <p className="text-dark-500 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: value.desc }} />
                </div>
              ))}
            </div>
          </div>

          {/* Why choose */}
          <div className="bg-dark-900 rounded-sm p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="font-display text-3xl lg:text-4xl text-white font-bold uppercase mb-4">Why buy from Tool Supply Point?</h2>
              <p className="text-dark-400 max-w-2xl mx-auto">We sweat the details so every order lands easy &mdash; from browse to first cut.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'A tight, hand-picked tool lineup',
                'Free USPS shipping across the USA',
                'Secure checkout with SSL encryption',
                '30-day hassle-free returns',
                'Friendly, responsive support',
                'Honest specs and descriptions',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-dark-800 rounded-sm border border-dark-700">
                  <CheckCircle2 className="w-5 h-5 text-athletic-500 flex-shrink-0" />
                  <span className="text-white font-medium">{item}</span>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/shop" className="inline-flex items-center gap-2 px-8 py-4 bg-athletic-600 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-athletic-700 transition-all shadow-button">Shop the tools</Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
